import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import {
  indexRange,
  relabelRange,
  restoreRange,
  withTrailingMarks,
} from '@src/utility/speakerRelabel';

import { LogManager } from '../../../utility/Logger';
import { ClipTranscriptSegment } from '../../models/ClipTranscriptSegment';
import { AbstractCommand } from '../AbstractCommand';

import type { ClipTranscriptToken } from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('transcript:speaker-reassign');

/**
 * Command that reassigns speaker labels on transcript tokens within a segment range.
 *
 * When `restoreOriginal` is false (reassign):
 *   - Sets `original_speaker_label` to preserve the current label (if not already set)
 *   - Overwrites `speaker_label` with the new value
 *
 * When `restoreOriginal` is true (undo reassign):
 *   - Restores `speaker_label` from `original_speaker_label`
 *   - Clears `original_speaker_label`
 *
 * Applies to every token in the range that carries a `speaker_label` — words
 * and punctuation alike (PLAT-11670); whitespace is never touched. Shares the
 * one rule with `useSpeakerOverrides` via `@src/utility/speakerRelabel.ts`.
 */
export class TranscriptSpeakerReassignCommand extends AbstractCommand {
  private readonly segmentId: string;
  private readonly fromIndex: number;
  private readonly toIndex: number;
  private readonly newSpeakerLabel: string;
  private readonly restoreOriginal: boolean;

  private element: VideoTranscriptElement | null = null;
  private previousSegment: ClipTranscriptSegment | null = null;

  constructor(
    editor: VideoEditor,
    segmentId: string,
    fromIndex: number,
    toIndex: number,
    newSpeakerLabel: string,
    restoreOriginal: boolean,
  ) {
    super(editor);
    this.segmentId = segmentId;
    this.fromIndex = Math.min(fromIndex, toIndex);
    this.toIndex = Math.max(fromIndex, toIndex);
    this.newSpeakerLabel = newSpeakerLabel;
    this.restoreOriginal = restoreOriginal;
  }

  execute(): boolean {
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) {
      logger.error('No transcript track available');
      return false;
    }

    const el = track.transcriptSegmentElements.find(
      e => e.getClipTranscriptSegment().id === this.segmentId,
    );
    if (!(el instanceof VideoTranscriptElement)) {
      logger.error('Segment element not found or invalid', {
        segmentId: this.segmentId,
      });
      return false;
    }
    this.element = el;

    const oldSegment = el.getClipTranscriptSegment();
    this.previousSegment = oldSegment;

    const tokens = oldSegment.getWordsWithPunctuation();
    if (this.fromIndex < 0 || this.toIndex > tokens.length - 1) {
      logger.error('Index range out of bounds', {
        fromIndex: this.fromIndex,
        toIndex: this.toIndex,
        tokenCount: tokens.length,
      });
      return false;
    }

    // Build new tokens with updated speaker labels. Words AND labelled
    // punctuation move together (PLAT-11670); the rule lives in
    // `@src/utility/speakerRelabel.ts`, shared with `useSpeakerOverrides`.
    //
    // The range is widened to the marks that belong to the selected words. A
    // drag selection stops at the last character of the last word, so the
    // sentence's closing full stop sits one index past it; without widening it
    // keeps the previous speaker's name and the row carries two names, which is
    // the refusal PLAT-11670 was filed for. The undo branch is widened by the
    // same rule so an undo covers exactly what the write covered.
    const range = withTrailingMarks(
      tokens,
      indexRange(this.fromIndex, this.toIndex),
    );
    const newTokens: ClipTranscriptToken[] = this.restoreOriginal
      ? restoreRange(tokens, range)
      : relabelRange(tokens, range, this.newSpeakerLabel);

    const newSegment = new ClipTranscriptSegment(
      newTokens,
      oldSegment.mediaStart,
      oldSegment.mediaEnd,
    );
    el.setClipTranscriptSegment(newSegment);

    this.recalculateTimeline();
    this.editor.markDirty('transcript_speaker_reassign');

    return true;
  }

  undo(): boolean {
    if (!this.element || !this.previousSegment) return false;
    this.element.setClipTranscriptSegment(this.previousSegment);
    this.recalculateTimeline();
    this.editor.markDirty('transcript_speaker_reassign_undo');
    return true;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  getName(): string {
    return 'TranscriptSpeakerReassignCommand';
  }

  getDescription(): string {
    if (this.restoreOriginal) {
      return `Restore original speaker for tokens (${this.fromIndex}-${this.toIndex}) in segment ${this.segmentId}`;
    }
    return `Reassign speaker to "${this.newSpeakerLabel}" for tokens (${this.fromIndex}-${this.toIndex}) in segment ${this.segmentId}`;
  }
}
